import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService, MfaSetupData } from '../auth.service';

/**
 * Sets up the authenticator app (QR code, first code) and then shows the recovery codes once. Used while logging in
 * (`mfaToken` set: mandatory setup, no session yet) and from the settings dialog (`mfaToken` null: signed in).
 * Brings its own dialog content/actions; the surrounding dialog only provides the title.
 */
@Component({
  selector: 'app-mfa-setup',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './mfa-setup.component.html',
  styleUrl: './mfa-setup.component.scss'
})
export class MfaSetupComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly snackBar = inject(MatSnackBar);

  @Input() mfaToken: string | null = null;
  /** The setup is done and the recovery codes were acknowledged. */
  @Output() completed = new EventEmitter<void>();
  /** The user backed out before finishing (or the login timed out: `expired`). */
  @Output() cancelled = new EventEmitter<{ expired: boolean }>();

  step: 'loading' | 'scan' | 'codes' = 'loading';
  setup: MfaSetupData | null = null;
  recoveryCodes: string[] = [];
  submitting = false;
  errorMessage: string | null = null;
  codesSaved = false;

  readonly code = new FormControl('', { nonNullable: true, validators: [Validators.required] });

  ngOnInit(): void {
    this.authService.startMfaSetup(this.mfaToken).subscribe({
      next: setup => {
        this.setup = setup;
        this.step = 'scan';
      },
      error: (error: HttpErrorResponse) => this.handleError(error)
    });
  }

  confirm(): void {
    if (this.code.invalid || this.submitting) {
      this.code.markAsTouched();
      return;
    }
    this.errorMessage = null;
    this.submitting = true;
    this.authService.confirmMfaSetup(this.mfaToken, this.code.value).subscribe({
      next: response => {
        this.submitting = false;
        this.recoveryCodes = response.recoveryCodes;
        this.step = 'codes';
      },
      error: (error: HttpErrorResponse) => {
        this.submitting = false;
        this.handleError(error);
      }
    });
  }

  copyCodes(): void {
    navigator.clipboard?.writeText(this.recoveryCodes.join('\n')).then(
      () => this.snackBar.open('Codes kopiert.', 'Ok', { duration: 3000 }),
      () => this.snackBar.open('Kopieren nicht möglich, bitte die Codes abschreiben.', 'Ok', { duration: 5000 })
    );
  }

  finish(): void {
    this.completed.emit();
  }

  cancel(): void {
    this.cancelled.emit({ expired: false });
  }

  private handleError(error: HttpErrorResponse): void {
    if (error.status === 401) {
      // only the login flow can run into this: the 5 minutes of the mfaToken are over
      this.cancelled.emit({ expired: true });
      return;
    }
    this.errorMessage = error.status === 400 || error.status === 429
      ? (error.error?.message || 'Der Code ist ungültig.')
      : 'Etwas ist schiefgelaufen. Bitte versuche es später erneut.';
  }
}
